import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserProfile from '../index';
import {
  RoleAssignmentView,
  RoleAssignmentStatus,
  BusinessRoleType,
  RoleSkillAlignmentView,
  getEmployeeRoleAssignments,
  getRoleSkillAlignment,
} from '../../../api/General/businessRoles';

interface Props {
  role: string;
  userId: string;
  selectedUser?: any;
  selectedUserManagers?: any[];
}

const roleTypeStyles: Record<BusinessRoleType, string> = {
  [BusinessRoleType.System]: 'bg-blue-100 text-blue-700',
  [BusinessRoleType.Project]: 'bg-purple-100 text-purple-700',
  [BusinessRoleType.Temporary]: 'bg-orange-100 text-orange-700',
};

const roleTypeLabels: Record<BusinessRoleType, string> = {
  [BusinessRoleType.System]: 'System',
  [BusinessRoleType.Project]: 'Project',
  [BusinessRoleType.Temporary]: 'Temporary',
};

function getAlignmentColor(percentage: number): string {
  if (percentage >= 80) return 'text-green-600';
  if (percentage >= 50) return 'text-amber-600';
  return 'text-red-600';
}

function getAlignmentBgColor(percentage: number): string {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function getAlignmentTrackColor(percentage: number): string {
  if (percentage >= 80) return 'bg-green-100';
  if (percentage >= 50) return 'bg-amber-100';
  return 'bg-red-100';
}

const ProfileBusinessRoles: React.FC<Props> = ({ selectedUser, userId }) => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState<RoleAssignmentView[]>([]);
  const [alignmentMap, setAlignmentMap] = useState<Record<number, RoleSkillAlignmentView>>({});
  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  const employeeId = selectedUser?.id || userId;

  const loadAssignments = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const result = await getEmployeeRoleAssignments(employeeId);
      if (result.errors.length === 0) {
        setAssignments(result.payload.items);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const loadAlignment = useCallback(async () => {
    if (!employeeId) return;
    try {
      const result = await getRoleSkillAlignment(employeeId);
      if (result.errors.length === 0) {
        const map: Record<number, RoleSkillAlignmentView> = {};
        for (const role of result.payload.roles) {
          map[role.businessRoleId] = role;
        }
        setAlignmentMap(map);
      }
    } catch { /* silent */ }
  }, [employeeId]);

  useEffect(() => {
    loadAssignments();
    loadAlignment();
  }, [loadAssignments, loadAlignment]);

  const activeAssignments = assignments.filter(a => a.status === RoleAssignmentStatus.Active);
  const pastAssignments = assignments.filter(a => a.status !== RoleAssignmentStatus.Active);

  const toggleExpanded = (roleId: number) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  return (
    <UserProfile>
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('businessRoles.myRoles')}</h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-sm text-gray-500">{t('businessRoles.noRolesAssigned')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Roles */}
            {activeAssignments.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">{t('businessRoles.currentRoles')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {activeAssignments.map((a) => {
                    const isExpiringSoon = a.expiryDate && new Date(a.expiryDate) <= new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                    const alignment = alignmentMap[a.businessRoleId];
                    const isExpanded = expandedRoles.has(a.businessRoleId);

                    return (
                      <div key={a.id} className={`p-4 rounded-xl border ${
                        isExpiringSoon ? 'border-amber-200 bg-amber-50' : 'border-border bg-card'
                      }`}>
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">{a.roleName}</h4>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${roleTypeStyles[a.roleType]}`}>
                            {t(`businessRoles.type${roleTypeLabels[a.roleType]}`)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <p>{t('businessRoles.since')}: {new Date(a.effectiveDate).toLocaleDateString()}</p>
                          {a.expiryDate && (
                            <p className={isExpiringSoon ? 'text-amber-600 font-medium' : ''}>
                              {t('businessRoles.expires')}: {new Date(a.expiryDate).toLocaleDateString()}
                              {isExpiringSoon && ` - ${t('businessRoles.expiringSoon')}`}
                            </p>
                          )}
                          {a.notes && <p className="text-gray-400 italic">{a.notes}</p>}
                        </div>

                        {/* Skill Alignment Section */}
                        {alignment && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <button
                              type="button"
                              onClick={() => toggleExpanded(a.businessRoleId)}
                              className="w-full text-left focus:outline-none"
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-gray-600">
                                  {t('businessRoles.skillMatch')}
                                </span>
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-xs font-semibold ${getAlignmentColor(alignment.alignmentPercentage)}`}>
                                    {alignment.matchedCount}/{alignment.totalRequired} ({alignment.alignmentPercentage}%)
                                  </span>
                                  <svg
                                    className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                              {/* Progress bar */}
                              <div className={`w-full h-1.5 rounded-full ${getAlignmentTrackColor(alignment.alignmentPercentage)}`}>
                                <div
                                  className={`h-1.5 rounded-full transition-all ${getAlignmentBgColor(alignment.alignmentPercentage)}`}
                                  style={{ width: `${alignment.alignmentPercentage}%` }}
                                />
                              </div>
                            </button>

                            {/* Expanded skill details */}
                            {isExpanded && (
                              <div className="mt-2.5 space-y-2">
                                {alignment.matchedSkills.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-green-700 mb-1">
                                      {t('businessRoles.matchedSkills')}
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {alignment.matchedSkills.map((skill) => (
                                        <span
                                          key={skill}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {alignment.missingSkills.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-red-700 mb-1">
                                      {t('businessRoles.missingSkills')}
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {alignment.missingSkills.map((skill) => (
                                        <span
                                          key={skill}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                          </svg>
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past Roles */}
            {pastAssignments.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase mb-3">{t('businessRoles.pastRoles')}</h3>
                <div className="space-y-2">
                  {pastAssignments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-gray-50">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700">{a.roleName}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(a.effectiveDate).toLocaleDateString()}
                          {a.expiryDate && ` - ${new Date(a.expiryDate).toLocaleDateString()}`}
                        </p>
                      </div>
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                        a.status === RoleAssignmentStatus.Revoked ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {t(`businessRoles.assignment${a.status === RoleAssignmentStatus.Revoked ? 'Revoked' : 'Expired'}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </UserProfile>
  );
};

const mapStateToProps = (state: any) => ({
  role: state.user.role,
  userId: state.user.userId,
  selectedUser: state.user.selectedUser,
  selectedUserManagers: state.user.selectedUserManagers,
});

export default connect(mapStateToProps)(ProfileBusinessRoles);
